// Berilgan paketlar (grantlar) RO'YXATI — SHARTNOMA-2 §B bo'yicha.
//
// GET /api/grants
//   ?subscriberId&agentId&planId&source&state&from&to&q
//   &sortField=STARTS_AT|EXPIRES_AT &sortOrder=ASC|DESC &page&size
//   → {rows:[GrantRow], page, size, (total YOKI hasNext)}
// Huquq: `subscriber.read` (`GrantController` da mavjud
// `Permission.SUBSCRIBER_READ`; yangi huquq O'YLAB TOPILMADI).
//
// ⚠️ SHARTNOMADA IKKI JOY OCHIQ QOLDIRILGAN va ikkalasi ham shu yerda
// KO'TARILADI:
//
//   1. `total` beriladimi yoki `hasNext` mi — hali tanlanmagan. Ikkalasi
//      ham `nullish`, natijani {@link toGrantPage} hal qiladi va jami son
//      NOMA'LUM bo'lsa uni CHIZMASLIK haqida `totalKnown: false` beradi.
//      Taxmin qilingan sonni "jami" deb ko'rsatish operator ishonadigan
//      yolg'on bo'lardi.
//   2. Eski qatorlarda `grantedByAgentId` BO'SH bo'lishi mumkin (ustun
//      yangi qo'shildi). Bo'sh maydon panelda CHIZIQCHA bo'lib chiqadi —
//      "noma'lum agent" DEYILMAYDI: yozuvda agent yo'q degani "agent
//      noma'lum" degani emas, operator ikkinchisini nosozlik deb o'qirdi.
//
// ⚠️ `default-property-inclusion: non_null` — bo'sh maydon JSON'da UMUMAN
// bo'lmaydi, ya'ni `.nullable()` YETMAYDI (u `null` ni kutadi, YO'QLIKNI
// emas). Hamma ixtiyoriy maydon `.nullish()`.
//
// ⚠️ `state`, `source`, `fundedBy` — `z.enum` EMAS, `z.string()`. Serverga
// yangi qiymat qo'shilsa `z.enum` BUTUN sahifani yiqitardi; bu yerda esa
// faqat o'sha katakda xom qiymat ko'rinadi va qolgan ustunlar ishlaydi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { syntheticTotal } from '@/shared/api/pageResult'
import { listParams } from '@/shared/api/serverPage'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

/** `grant.enums.GrantSource` */
export const GRANT_SOURCES = ['PLAN', 'VOUCHER', 'AD_VIEW', 'VENUE_COMP', 'MANUAL'] as const
export type GrantSourceValue = (typeof GRANT_SOURCES)[number]

/** `grant.enums.FundedBy` */
export const FUNDED_BY = ['SUBSCRIBER', 'VENUE', 'ADVERTISER', 'NONE'] as const
export type FundedByValue = (typeof FUNDED_BY)[number]

/**
 * ⚠️ `EXPIRED` VA `EXHAUSTED` SUKUT BO'YICHA YASHIRILMAYDI (ADR-033).
 *
 * ADR-033 istisnolar jadvalida aynan shu holat bor: «Vaucher partiyasi —
 * muddati o'tgani TARIX, yopilgan emas». Grant ro'yxati ham reyestr emas,
 * TARIX: u "abonent qanday paketlar oldi" va "agent nechta paket sotdi"
 * degan savollarga javob beradi. Muddati o'tganini yashirish bir yillik
 * abonentda ro'yxatni BO'SHATIB qo'yardi va operator buni "abonent hech
 * narsa olmagan" deb o'qirdi — ya'ni ADR-033 aynan to'sishga urinayotgan
 * jimgina yolg'on.
 *
 * ADR-033 ning boshqa bandlari ham bu ro'yxatga tushmaydi: §6 «sanoqlar
 * yopilganlarni sanamaydi» tarix jurnalida ma'nosiz, §5 «yashiringan
 * qator soni» esa yashiradigan narsa bo'lmagani uchun keraksiz.
 * Qoidaning ASOSI (§2 — «yagona yo'l holat filtri») esa QO'LLANADI:
 * holatni tanlashning yagona yo'li — holat filtri, alohida belgilagich
 * yo'q.
 */
export const GRANT_LIST_STATES = [
  'PENDING',
  'ACTIVE',
  'EXHAUSTED',
  'EXPIRED',
  'REVOKED',
] as const
export type GrantListState = (typeof GRANT_LIST_STATES)[number]

const grantRowSchema = z.object({
  id: uuidSchema,
  subscriberId: uuidSchema,
  subscriberLogin: z.string().nullish(),
  planId: uuidSchema.nullish(),
  planName: z.string().nullish(),
  grantedByAgentId: uuidSchema.nullish(),
  grantedByAgentName: z.string().nullish(),
  source: z.string().nullish(),
  fundedBy: z.string().nullish(),
  startsAt: instantSchema.nullish(),
  expiresAt: instantSchema.nullish(),
  quotaBytes: z.number().nullish(),
  state: z.string().nullish(),
})

/**
 * ⚠️ `total` VA `hasNext` — IKKALASI HAM ixtiyoriy va bu ATAYLAB.
 * Shartnomada qaysi biri berilishi hali tanlanmagan; sxema ikkalasini
 * ham qabul qiladi, {@link toGrantPage} esa qaysi biri kelganiga qarab
 * jami sonni ANIQ yoki NOMA'LUM deb belgilaydi.
 */
const grantPageSchema = z.object({
  rows: z.array(grantRowSchema),
  page: z.number().nullish(),
  size: z.number().nullish(),
  total: z.number().nullish(),
  hasNext: z.boolean().nullish(),
})

export interface GrantRow {
  id: string
  subscriberId: string
  subscriberLogin: string | null
  planId: string | null
  planName: string | null
  /** `null` — yozuvda agent YO'Q. "Noma'lum" EMAS. */
  grantedByAgentId: string | null
  grantedByAgentName: string | null
  source: string | null
  fundedBy: string | null
  startsAt: string | null
  expiresAt: string | null
  quotaBytes: number | null
  state: string | null
}

function toGrantRow(raw: z.infer<typeof grantRowSchema>): GrantRow {
  return {
    id: raw.id,
    subscriberId: raw.subscriberId,
    subscriberLogin: raw.subscriberLogin ?? null,
    planId: raw.planId ?? null,
    planName: raw.planName ?? null,
    grantedByAgentId: raw.grantedByAgentId ?? null,
    grantedByAgentName: raw.grantedByAgentName ?? null,
    source: raw.source ?? null,
    fundedBy: raw.fundedBy ?? null,
    startsAt: raw.startsAt ?? null,
    expiresAt: raw.expiresAt ?? null,
    quotaBytes: raw.quotaBytes ?? null,
    state: raw.state ?? null,
  }
}

/**
 * Ustun kaliti → server `sortField` enumi.
 *
 * ⚠️ `CREATED_AT` YO'Q: `GET /api/grants` uni QABUL QILMAYDI (shartnoma
 * §B faqat `STARTS_AT` va `EXPIRES_AT` ni beradi). "Oxirgi berilgani
 * birinchi" qoidasi `STARTS_AT DESC` bilan qondiriladi — grant
 * boshlanish oni bilan yoziladi, ya'ni tartib amalda bir xil.
 */
export const GRANT_SORT_FIELDS = {
  startsAt: 'STARTS_AT',
  expiresAt: 'EXPIRES_AT',
} as const

const UUID_SHAPE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

/**
 * ⚠️ Shakli buzuq id SERVERGA YUBORILMAYDI. Kontrollerda `@RequestParam
 * UUID`, ya'ni qo'lda tahrirlangan havola 400 berardi va butun sahifa
 * xatoga uchrardi — filtr esa faqat bittasi edi.
 */
function uuidFilter(values: readonly string[] | undefined): string | undefined {
  const raw = values?.[0]
  return raw !== undefined && UUID_SHAPE.test(raw) ? raw : undefined
}

function enumFilter<T extends string>(
  values: readonly string[] | undefined,
  allowed: readonly T[],
): T | undefined {
  const raw = values?.[0]
  return allowed.find((value) => value === raw)
}

/**
 * ⚠️ Onlar (`from`/`to`) TEKSHIRILADI. Havolada `from=kecha` tursa server
 * 400 berardi; bu yerda esa yaroqsiz qiymat shunchaki YUBORILMAYDI va
 * ro'yxat ochiladi.
 */
function instantFilter(values: readonly string[] | undefined): string | undefined {
  const raw = values?.[0]
  if (raw === undefined) return undefined
  return Number.isNaN(Date.parse(raw)) ? undefined : raw
}

/**
 * Jadval holati → `GET /api/grants` parametrlari.
 *
 * ⚠️ FUNKSIYA ATAYLAB AJRATILGAN va eksport qilingan: qorovul uni
 * CHAQIRIB o'lchaydi (`tests/grants-page.mjs`). Manba matnida naqsh
 * qidirish "sahifa serverga ketyaptimi" degan savolga javob bermasdi —
 * `subscriberListParams` dagi bir xil saboq.
 *
 * ⚠️ `page`/`size` HAR DOIM boradi (`listParams`), ya'ni sahifalash
 * SERVERDA. Mijozda kesish TAQIQ (JADVAL-QOIDASI §6).
 */
export function grantListParams(state: TableState): Record<string, unknown> {
  const filters = state.filters
  return {
    ...listParams(state, { sortFields: GRANT_SORT_FIELDS, searchParam: 'q' }),
    ...optional('subscriberId', uuidFilter(filters['subscriberId'])),
    ...optional('agentId', uuidFilter(filters['agentId'])),
    ...optional('planId', uuidFilter(filters['planId'])),
    ...optional('source', enumFilter(filters['source'], GRANT_SOURCES)),
    ...optional('state', enumFilter(filters['state'], GRANT_LIST_STATES)),
    ...optional('from', instantFilter(filters['from'])),
    ...optional('to', instantFilter(filters['to'])),
  }
}

function optional(name: string, value: string | undefined): Record<string, string> {
  return value === undefined ? {} : { [name]: value }
}

export interface GrantPage extends PageResult<GrantRow> {
  /**
   * Server jami sonni ROSTDAN aytdimi.
   *
   * `false` bo'lsa `total` — sahifalash tugmalari ishlashi uchun yasalgan
   * SUN'IY son va u ekranga "jami N" deb CHIQMASLIGI kerak.
   */
  totalKnown: boolean
}

/**
 * Javob konvertini `DataTable` kutadigan shaklga keltiradi.
 *
 * Uch holat:
 *   · `total` bor        → aniq son, "1–25 / 1 240" chiziladi;
 *   · `hasNext` bor      → sun'iy son (`syntheticTotal`), jami CHIZILMAYDI;
 *   · ikkalasi ham yo'q  → to'la sahifa "yana bor" deb o'qiladi. Bu
 *     TAXMIN, shuning uchun `totalKnown: false` va jami baribir
 *     chizilmaydi. Aks holda oxirgi sahifadan keyin yurish yo'li
 *     yo'qolardi va operator ro'yxat tugadi deb o'ylardi.
 */
export function toGrantPage(state: TableState, parsed: unknown): GrantPage {
  const page = grantPageSchema.parse(parsed)
  const items = page.rows.map(toGrantRow)

  if (typeof page.total === 'number') {
    return { items, total: page.total, totalKnown: true }
  }

  const hasNext = page.hasNext ?? items.length >= state.pageSize
  return {
    items,
    total: syntheticTotal(state.page, state.pageSize, items.length, hasNext),
    totalKnown: false,
  }
}

/**
 * Bir sahifa grant.
 *
 * @param onShape jami son ANIQ ekanini sahifaga qaytaradi — `DataTable`
 *   `showTotal` ni chizishdan OLDIN biladi. Javobning shakli faqat javob
 *   kelganda ma'lum bo'ladi, prop esa undan oldin beriladi; shuning uchun
 *   bu yagona yo'l.
 */
export function fetchGrantPage(onShape: (totalKnown: boolean) => void) {
  return async (state: TableState, signal: AbortSignal): Promise<PageResult<GrantRow>> => {
    const response = await api.get<unknown>('/api/grants', {
      signal,
      params: grantListParams(state),
    })
    const page = toGrantPage(state, response.data)
    onShape(page.totalKnown)
    return { items: page.items, total: page.total }
  }
}
